import React, { useState, useEffect } from "react";
import { Button } from "../ui/button";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from "../ui/dialog";
import { generateMockObject } from "../../utils/mockDataGenerator";
import { toast } from "../../hooks/use-toast";
import { motion, AnimatePresence } from "framer-motion";
import { AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent, AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle } from "../ui/alert-dialog";
import { Check, AlertTriangle, CrossIcon, Cross, X } from "lucide-react";

interface ObjectEditorProps {
    object: any;
    onChange: (object: any) => void;
    onSave: (object: any) => void;
    onCreate: (object: any) => void;
    objectType: string;
}

export function ObjectEditor({
    object,
    onChange,
    onSave,
    onCreate,
    objectType,
}: ObjectEditorProps) {
    const [jsonError, setJsonError] = useState<string | null>(null);
    const [jsonString, setJsonString] = useState<string>("");
    const [showCreateDialog, setShowCreateDialog] = useState(false);
    const [newObjectJson, setNewObjectJson] = useState<string>("");
    const [newObjectError, setNewObjectError] = useState<string | null>(null);
    const [showSaveConfirmation, setShowSaveConfirmation] = useState(false);
    const [jsonIsValid, setJsonIsValid] = useState(true);
    const [lineCount, setLineCount] = useState(0);
    const [currentParsedObject, setCurrentParsedObject] = useState<any>(null);

    useEffect(() => {
        if (object) {
            const formatted = JSON.stringify(object, null, 2);
            setJsonString(formatted);
            setLineCount(formatted.split('\n').length);
            setCurrentParsedObject(object);
        } else {
            setJsonString("");
        }
    }, [object]);

    const validateJson = (json: string) => {
        try {
            const parsed = JSON.parse(json);
            setJsonIsValid(true);
            setJsonError(null);
            setCurrentParsedObject(parsed);
            return parsed;
        } catch (error) {
            setJsonIsValid(false);
            if (error instanceof Error) {
                setJsonError(error.message);
            } else {
                setJsonError("Invalid JSON");
            }
            return null;
        }
    };

    const handleJsonChange = (e: React.ChangeEvent<HTMLTextAreaElement>) => {
        const newJsonString = e.target.value;
        setJsonString(newJsonString);
        setLineCount(newJsonString.split('\n').length);

        try {
            const parsed = JSON.parse(newJsonString);
            onChange(parsed);
            setJsonIsValid(true);
            setJsonError(null);
            setCurrentParsedObject(parsed);
        } catch (error) {
            setJsonIsValid(false);
            if (error instanceof Error) {
                setJsonError(error.message);
            } else {
                setJsonError("Invalid JSON");
            }
        }
    };

    const handleCreateObject = () => {
        const parsed = validateJson(newObjectJson);
        if (parsed) {
            onCreate(parsed);
            setShowCreateDialog(false);
            setNewObjectJson("");
            setNewObjectError(null);
            toast({
                description: `New ${objectType} created successfully`,
                variant: "default",
            });
        }
    };

    const handleSaveClick = () => {
        const parsed = validateJson(jsonString);
        if (parsed) {
            setShowSaveConfirmation(true);
        } else {
            toast({
                description: "Cannot save with invalid JSON",
                variant: "destructive",
            });
        }
    };

    const confirmSave = () => {
        if (jsonIsValid && currentParsedObject) {
            onSave(currentParsedObject);
            setShowSaveConfirmation(false);
            toast({
                description: "Changes saved successfully",
                variant: "default",
            });
        }
    };

    const handleGenerateTemplate = () => {
        const template = generateMockObject(objectType);
        setNewObjectJson(JSON.stringify(template, null, 2));
    };

    const renderLineNumbers = () => {
        return (
            <div className="absolute left-0 top-8 bottom-0 w-12 bg-[#1e1e1e] border-r border-[#333333] pt-0 text-right select-none">
                {Array.from({ length: lineCount }).map((_, i) => (
                    <div key={i} className="text-[#858585] text-xs px-2">
                        {i + 1}
                    </div>
                ))}
            </div>
        );
    };

    if (!object) {
        return (
            <motion.div
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.3 }}
                className="flex flex-col items-center justify-center h-full bg-muted/20 rounded-md border border-dashed overflow-auto"
            >
                <p className="text-muted-foreground mb-4">No object selected</p>
                <Dialog open={showCreateDialog} onOpenChange={setShowCreateDialog}>
                    <DialogTrigger asChild>
                        <Button className="animate-scale-in">Create New {objectType}</Button>
                    </DialogTrigger>
                    <DialogContent className="max-w-3xl animate-scale-in">
                        <DialogHeader>
                            <DialogTitle>Create New {objectType}</DialogTitle>
                        </DialogHeader>
                        <div className="grid gap-4">
                            <div className="flex justify-end">
                                <Button variant="outline" size="sm" onClick={handleGenerateTemplate}>
                                    Generate Template
                                </Button>
                            </div>
                            <div className="relative">
                                <div className="border rounded-md bg-[#1e1e1e] text-[#d4d4d4] font-mono p-4 h-96 overflow-auto">
                                    <textarea
                                        className="w-full h-full bg-transparent resize-none focus:outline-none"
                                        value={newObjectJson}
                                        onChange={(e) => {
                                            setNewObjectJson(e.target.value);
                                            validateJson(e.target.value);
                                        }}
                                        placeholder={`{\n  "id": "unique-id",\n  "name": "Enter name",\n  ...\n}`}
                                        spellCheck="false"
                                    />
                                </div>
                                {newObjectError && (
                                    <motion.div
                                        initial={{ opacity: 0 }}
                                        animate={{ opacity: 1 }}
                                        transition={{ duration: 0.3 }}
                                        className="text-destructive text-sm mt-2 bg-destructive/10 p-2 rounded-md flex items-center gap-2"
                                    >
                                        <AlertTriangle size={16} />
                                        <span>Error: {newObjectError}</span>
                                    </motion.div>
                                )}
                            </div>
                            <div className="flex justify-end gap-2">
                                <Button
                                    variant="outline"
                                    onClick={() => setShowCreateDialog(false)}
                                >
                                    Cancel
                                </Button>
                                <Button
                                    onClick={handleCreateObject}
                                    disabled={!!newObjectError || !newObjectJson.trim()}
                                >
                                    Create Object
                                </Button>
                            </div>
                        </div>
                    </DialogContent>
                </Dialog>
            </motion.div>
        );
    }

    return (
        <>

            <div className="flex justify-between p-4 ">
                <Button
                    variant="outline"
                    onClick={() => {
                        if (object) {
                            setJsonString(JSON.stringify(object, null, 2));
                            setJsonError(null);
                            setJsonIsValid(true);
                            toast({
                                description: "Changes reset",
                                variant: "default",
                            });
                        }
                    }}
                    className="transition-all duration-200 hover:scale-105"
                >
                    Reset
                </Button>
                <Button
                    onClick={handleSaveClick}
                    disabled={!jsonIsValid}
                    className="transition-all duration-200 hover:scale-105"
                >
                    Save Changes
                </Button>
            </div>
            <motion.div
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.3 }}
                className="h-full flex flex-col space-y-4 min-h-[60vh]"
            >
                <div className="flex-grow relative">
                    <div className="border rounded-md bg-[#1e1e1e] text-[#d4d4d4] font-mono p-4 h-full overflow-auto transition-all duration-200 shadow-lg">
                        <div className="absolute top-0 right-0 left-0 h-8 bg-[#252526] border-b border-[#1e1e1e] rounded-t-md px-4 flex items-center justify-between">
                            <span className="text-sm opacity-60">JSON Editor</span>
                            <div className="flex items-center">
                                {jsonIsValid ? (
                                    <motion.div
                                        initial={{ opacity: 0 }}
                                        animate={{ opacity: 1 }}
                                        className="text-green-500 flex items-center gap-1 text-xs mr-2"
                                    >
                                        <Check size={12} />
                                        <span>Valid JSON</span>
                                    </motion.div>
                                ): (
                                    <motion.div
                                        initial={{ opacity: 0 }}
                                        animate={{ opacity: 1 }}
                                        className="text-red-600 flex items-center gap-1 text-xs mr-2"
                                    >
                                        <X size={12} />
                                        <span>In Valid JSON</span>
                                    </motion.div>
                                )}
                            </div>
                        </div>
                        <div className="pt-8 h-[calc(100%-2rem)] pl-12 relative">
                            {renderLineNumbers()}
                            <textarea
                                className="w-full h-full bg-transparent resize-none focus:outline-none text-sm leading-relaxed selection:bg-[#264f78] selection:text-white"
                                value={jsonString}
                                onChange={handleJsonChange}
                                spellCheck="false"
                                style={{
                                    caretColor: '#fff',
                                    tabSize: 2,
                                }}
                            />
                        </div>
                    </div>
                    <AnimatePresence>
                        {jsonError && (
                            <motion.div
                                initial={{ opacity: 0, y: 10 }}
                                animate={{ opacity: 1, y: 0 }}
                                exit={{ opacity: 0, y: 10 }}
                                transition={{ duration: 0.2 }}
                                className="text-destructive text-sm mt-2 bg-destructive/10 p-2 rounded-md flex items-center gap-2 animate-pulse"
                            >
                                <AlertTriangle size={16} />
                                <span>Error: {jsonError}</span>
                            </motion.div>
                        )}
                    </AnimatePresence>
                </div>
                <div className="flex justify-between">
                    <Button
                        variant="outline"
                        onClick={() => {
                            if (object) {
                                setJsonString(JSON.stringify(object, null, 2));
                                setJsonError(null);
                                setJsonIsValid(true);
                                toast({
                                    description: "Changes reset",
                                    variant: "default",
                                });
                            }
                        }}
                        className="transition-all duration-200 hover:scale-105"
                    >
                        Reset
                    </Button>
                    <Button
                        onClick={handleSaveClick}
                        disabled={!jsonIsValid}
                        className="transition-all duration-200 hover:scale-105"
                    >
                        Save Changes
                    </Button>
                </div>

                <AlertDialog open={showSaveConfirmation} onOpenChange={setShowSaveConfirmation}>
                    <AlertDialogContent>
                        <AlertDialogHeader>
                            <AlertDialogTitle>Confirm Save Changes</AlertDialogTitle>
                            <AlertDialogDescription>
                                Are you sure you want to save these changes to the {objectType}?
                            </AlertDialogDescription>
                        </AlertDialogHeader>
                        <AlertDialogFooter>
                            <AlertDialogCancel>Cancel</AlertDialogCancel>
                            <AlertDialogAction onClick={confirmSave}>Save Changes</AlertDialogAction>
                        </AlertDialogFooter>
                    </AlertDialogContent>
                </AlertDialog>
            </motion.div>
        </>
    );
}
